import { DS2 } from '../../../theme/designTokens';
import { API_CONFIG } from '../../../config/apiConfig';
import { relativeTimeShort } from '../../../utils/relativeTime';
import { renderFormattedMessage } from '../../../utils/messageFormatting';
import type { DirectMessage } from '../../../types/social';

interface Props {
  messages: DirectMessage[];
  /** The counterpart's user id — a message the counterpart RECEIVED is mine. */
  friendId: number;
  /** Opens the shared image viewer (ImageLightbox) — owned by the caller. */
  onImageClick: (url: string) => void;
  /** Dock windows use slightly tighter bubbles; the /messages screen keeps
   *  its exact original metrics (compact absent ⇒ byte-identical styles). */
  compact?: boolean;
}

/**
 * The ONE shared direct-message bubble renderer — extracted verbatim from the
 * /messages MessageThread so the dock windows and the legacy screen can never
 * drift apart on message formatting, image handling, or hostile-content
 * safety (bodies render through renderFormattedMessage, never raw HTML).
 * The scroll container, loading/empty branches and lightbox state stay with
 * each host — this component renders only the rows.
 */
export function DirectMessageBubbles({ messages, friendId, onImageClick, compact }: Props) {
  return (
    <>
      {messages.map(m => {
        const mine = m.recipient_id === friendId;
        return (
          <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
            <div
              style={{
                maxWidth:     compact ? '85%' : '72%',
                padding:      m.image_url ? 6 : '9px 13px',
                borderRadius: 14,
                background:   mine ? DS2.gradient.gold : 'rgba(245,245,245,0.08)',
                color:        mine ? '#0B0B0B' : '#FFFFFF',
                fontSize:     13.5,
                fontFamily:   DS2.font.body,
                lineHeight:   1.4,
                wordBreak:    'break-word',
              }}
            >
              {m.image_url && (
                <img
                  src={`${API_CONFIG.baseUrl}${m.image_url}`}
                  alt=""
                  onClick={() => onImageClick(`${API_CONFIG.baseUrl}${m.image_url}`)}
                  style={{
                    display:      'block',
                    maxWidth:     compact ? 180 : 220,
                    maxHeight:    compact ? 180 : 220,
                    borderRadius: 10,
                    objectFit:    'cover',
                    cursor:       'zoom-in',
                  }}
                />
              )}
              {m.body && <div style={{ padding: m.image_url ? '6px 5px 2px' : 0, whiteSpace: 'pre-wrap' }}>{renderFormattedMessage(m.body)}</div>}
            </div>
            <span style={{ marginTop: 3, fontSize: 10.5, fontFamily: DS2.font.body, color: '#6B6B6B' }}>
              {relativeTimeShort(m.created_at)}
            </span>
          </div>
        );
      })}
    </>
  );
}
